"use client";
/**
 * Shared building blocks for the Block 5 loyalty surfaces (hub, challenges,
 * badges, referrals, wallet pass). Visual language is ported 1:1 from the
 * design handoff at design-handoffs/2026-05-28-loyalty-expansion/.
 */
import * as React from "react";
import { useEffect } from "react";
import type { TFunction } from "i18next";

import { Icons } from "@/components/Icons";
import { Button } from "@/components/ui";
import { SignInModal } from "@/components/SignInModal";
import type { LoyaltyReward2, ResolvedBenefit } from "@/api/services";

/* ──────── Tier badge (gradient pill) ──────── */

const TIER_GRADIENTS: Record<string, string> = {
  bronze: "linear-gradient(135deg, #c08862, #7c4a2a)",
  silver: "linear-gradient(135deg, #d4d8df, #8a93a3)",
  gold: "linear-gradient(135deg, #f4d68a, #c9a449)",
  platinum: "linear-gradient(135deg, #98a3b6, #2c3340)",
};

export function tierGradient(tier: string | null | undefined): string {
  const key = (tier ?? "").trim().toLowerCase();
  return TIER_GRADIENTS[key] ?? TIER_GRADIENTS.gold;
}

export function TierBadge({
  tier,
  style,
}: {
  tier: string;
  style?: React.CSSProperties;
}) {
  const isPlatinum = tier.trim().toLowerCase() === "platinum";
  return (
    <span
      className="display"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 12px",
        borderRadius: 999,
        background: tierGradient(tier),
        color: isPlatinum ? "#f4f6fa" : "#1e1810",
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.4), 0 1px 2px rgba(0,0,0,0.15)",
        ...style,
      }}
    >
      {tier}
    </span>
  );
}

/* ──────── Bottom sheet (scrim + panel + grab handle) ──────── */

export function BottomSheet({
  open,
  onClose,
  children,
  label,
}: {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  label?: string;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(10, 14, 12, 0.45)",
          backdropFilter: "blur(2px)",
          WebkitBackdropFilter: "blur(2px)",
          zIndex: 30,
          animation: "fadeIn 180ms ease",
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={label}
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          background: "var(--surface-1)",
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          padding: "16px 18px calc(env(safe-area-inset-bottom, 0px) + 22px)",
          boxShadow: "0 -10px 30px -10px rgba(0,0,0,0.25)",
          zIndex: 31,
          maxHeight: "85%",
          overflowY: "auto",
          animation: "slideUp 220ms cubic-bezier(0.2, 0.8, 0.2, 1)",
        }}
      >
        <div
          aria-hidden
          style={{
            width: 40,
            height: 4,
            borderRadius: 999,
            background: "var(--surface-2-strong)",
            margin: "2px auto 14px",
          }}
        />
        {children}
      </div>
    </>
  );
}

/* ──────── Icon chip (the design's `.iconc`) ──────── */

export type IconChipTone = "primary" | "surface" | "gold" | "flame" | "dark" | "amber";

const CHIP_TONES: Record<IconChipTone, { bg: string; fg: string }> = {
  primary: { bg: "var(--primary-50)", fg: "var(--primary-700)" },
  surface: { bg: "var(--surface-2)", fg: "var(--ink-2)" },
  gold: { bg: "#fbf2da", fg: "#82610f" },
  flame: { bg: "#fff0e7", fg: "#e8552d" },
  dark: { bg: "#14171a", fg: "#fff" },
  amber: { bg: "#fff5e2", fg: "#b4641a" },
};

export function IconChip({
  tone = "primary",
  size = 40,
  radius = 12,
  children,
  style,
}: {
  tone?: IconChipTone;
  size?: number;
  radius?: number;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  const t = CHIP_TONES[tone];
  return (
    <div
      style={{
        display: "grid",
        placeItems: "center",
        width: size,
        height: size,
        borderRadius: radius,
        background: t.bg,
        color: t.fg,
        flexShrink: 0,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/* ──────── Benefit metadata (icon + copy per benefit type) ──────── */

type IconComponent = (p: { size?: number; style?: React.CSSProperties }) => React.ReactNode;

export type BenefitMeta = {
  icon: IconComponent;
  tone: IconChipTone;
  title: string;
  sub: string;
};

export function benefitMeta(b: ResolvedBenefit, t: TFunction): BenefitMeta {
  const params = (b.params ?? {}) as Record<string, unknown>;
  switch (b.type) {
    case "free_delivery":
      return {
        icon: Icons.truck,
        tone: "primary",
        title: t("v2.loyalty_hub.benefit_free_delivery"),
        sub: t("v2.loyalty_hub.benefit_free_delivery_sub"),
      };
    case "priority_queue":
      return {
        icon: Icons.bolt,
        tone: "gold",
        title: t("v2.loyalty_hub.benefit_priority_queue"),
        sub: t("v2.loyalty_hub.benefit_priority_queue_sub"),
      };
    case "vip_table":
      return {
        icon: Icons.chair,
        tone: "primary",
        title: t("v2.loyalty_hub.benefit_vip_table"),
        sub: t("v2.loyalty_hub.benefit_vip_table_sub"),
      };
    case "early_access_menu": {
      const days = typeof params.daysBeforePublic === "number" ? params.daysBeforePublic : 7;
      return {
        icon: Icons.spark,
        tone: "primary",
        title: t("v2.loyalty_hub.benefit_early_access"),
        sub: t("v2.loyalty_hub.benefit_early_access_sub", { count: days }),
      };
    }
    case "birthday_free_item": {
      const days = typeof params.windowDays === "number" ? params.windowDays : 7;
      return {
        icon: Icons.cake,
        tone: "gold",
        title: t("v2.loyalty_hub.benefit_birthday"),
        sub: t("v2.loyalty_hub.benefit_birthday_sub", { count: days }),
      };
    }
    case "points_multiplier": {
      const mult = typeof params.multiplier === "number" ? params.multiplier : 2;
      return {
        icon: Icons.spark,
        tone: "primary",
        title: t("v2.loyalty_hub.benefit_points_multiplier", { multiplier: mult }),
        sub: t("v2.loyalty_hub.benefit_points_multiplier_sub", { multiplier: mult }),
      };
    }
    default:
      return {
        icon: Icons.gift,
        tone: "primary",
        title: String(b.type ?? "").replace(/[_-]+/g, " ").replace(/\b\w/g, (m) => m.toUpperCase()),
        sub: "",
      };
  }
}

/* ──────── Reward description (points / promo / free item union) ──────── */

export function describeReward(
  reward: LoyaltyReward2 | null | undefined,
  t: TFunction,
): string | null {
  if (!reward || typeof reward !== "object") return null;
  const r = reward as { type?: string; amount?: number };
  switch (r.type) {
    case "points":
      return t("v2.loyalty_hub.reward_points", { count: r.amount ?? 0 });
    case "promo_code":
      return t("v2.loyalty_hub.reward_promo");
    case "free_item":
      return t("v2.loyalty_hub.reward_free_item");
    default:
      return null;
  }
}

/* ──────── Signed-out gate (shared across the new screens) ──────── */

export function SignedOutGate({
  icon,
  title,
  desc,
  cta,
}: {
  icon: React.ReactNode;
  title: string;
  desc: string;
  cta: string;
}) {
  const [signInOpen, setSignInOpen] = React.useState(false);
  return (
    <>
      <div
        style={{
          padding: "42px 24px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          gap: 14,
        }}
      >
        <div
          style={{
            width: 76,
            height: 76,
            borderRadius: 999,
            background: "var(--primary-50)",
            display: "grid",
            placeItems: "center",
            color: "var(--primary-700)",
          }}
        >
          {icon}
        </div>
        <div className="display" style={{ fontSize: 18, fontWeight: 700, color: "var(--ink-1)" }}>
          {title}
        </div>
        <div style={{ maxWidth: 280, fontSize: 13, lineHeight: 1.45, color: "var(--ink-2)" }}>
          {desc}
        </div>
        <Button size="lg" onClick={() => setSignInOpen(true)}>
          {cta}
        </Button>
      </div>
      <SignInModal open={signInOpen} onClose={() => setSignInOpen(false)} />
    </>
  );
}

/* ──────── Progress bar ──────── */

export function ProgressBar({
  value,
  height = 8,
  dark,
}: {
  /** 0..1 */
  value: number;
  height?: number;
  dark?: boolean;
}) {
  const pct = Math.max(0, Math.min(1, value)) * 100;
  return (
    <div
      style={{
        height,
        background: dark ? "rgba(255,255,255,0.16)" : "var(--surface-2-strong)",
        borderRadius: 999,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: `${pct}%`,
          height: "100%",
          background: dark ? "var(--primary-400)" : "var(--primary-500)",
          borderRadius: 999,
          transition: "width 300ms ease",
        }}
      />
    </div>
  );
}
